import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Modal } from "flowbite-react";
import Button from "../../../components/button/button";
import TextBox from "../../../components/forms/text-box";
import TextArea from "../../../components/forms/text-area";
import Message from "../../../components/toast-message";
import FormLoader from "../../../components/form-loader/form-loader";
import { useAppInfo } from "../../../helpers/hooks/common-hook";
import {
    getPromptConfigTool,
    createPromptConfigTool,
    updatePromptConfigTool,
} from "../../../services/prompt-ai";
import { SUB_AGENT_BASE } from "../../../consts/agent-nav-config";
import { SUB_AGENT_TOOL_TYPE } from "../../../helpers/hooks/sub-agent-hook";

/**
 * Create / rename a sub-agent.
 *
 * Mirrors the workflow (journey) create dialog: collect a name and description,
 * save, then jump into the newly created sub-agent's config surface. The deeper
 * configuration — instructions and tools — lives on the scoped tabs, so this
 * dialog deliberately stays to those two fields.
 */
function SubAgentCreateModel({ onCompletion, onCancel, subAgentId }) {
    const rootRef = useRef(null);
    const navigate = useNavigate();
    const appInfo = useAppInfo();
    const { appId } = appInfo;

    const [formData, setFormData] = useState({
        name: undefined,
        description: undefined,
    });

    // preserved across an edit so saving a rename does not wipe the config
    const [existing, setExisting] = useState(null);

    const [createdSubAgentId, setCreatedSubAgentId] = useState();

    const [dataLoadingState, setDataLoading] = useState({
        processing: false,
        success: false,
        failed: false,
        message: null
    });

    const [error, setError] = useState({
        name: false,
        nameErrorMessage: null,
        description: false
    });

    const [formState, setFormState] = useState({
        processing: false,
        success: false,
        failed: false,
        message: ''
    });

    useEffect(() => {
        if (createdSubAgentId) {
            setTimeout(() => {
                onCompletion()
                navigate(`/app/${appId}${SUB_AGENT_BASE}/${createdSubAgentId}`)
            }, 1500)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createdSubAgentId]);

    const handleChange = useCallback(event => {
        const targetName = event.target.name;
        const targetValue = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData((prev) => ({ ...prev, [targetName]: targetValue }));
    }, []);

    const validateForm = () => {
        let valid = true;
        if (!formData.name) {
            error.name = true;
            valid = false;
        } else {
            error.name = false;
        }

        if (!formData.description) {
            error.description = true;
            valid = false;
        } else {
            error.description = false;
        }

        setError({ ...error })
        return valid;
    }

    const updateFormState = (processing, success, failed, message) => {
        setFormState(Object.assign({}, { processing, success, failed, message }))
    }

    const updateDataLoadingState = (processing, success, failed, message) => {
        setDataLoading(Object.assign({}, { processing, success, failed, message }))
    }

    const handleOnSubmit = async (event) => {
        event.preventDefault();
        if (formState.processing || !validateForm()) return;

        updateFormState(true, false, false);

        const payload = {
            name: formData.name.trim(),
            type: SUB_AGENT_TOOL_TYPE,
            description: (formData.description || '').trim(),
            // keep whatever the sub-agent already had; this dialog only owns
            // name + description
            parameters: existing?.parameters || {},
            toolData: existing?.toolData || {},
        };

        const { status, response } = subAgentId
            ? await updatePromptConfigTool(appId, subAgentId, payload)
            : await createPromptConfigTool(appId, payload);

        if (status === 200 || status === 201) {
            if (subAgentId) {
                updateFormState(false, true, false, 'Successfully saved sub-agent');
                setTimeout(() => onCompletion(), 800);
            } else {
                setCreatedSubAgentId(response?.id);
                updateFormState(false, true, false, 'Successfully saved sub-agent');
            }
            return true;
        }

        updateFormState(false, false, true, 'Unable to save sub-agent');
        return false;
    };

    async function fetchSubAgent() {
        updateDataLoadingState(true, false, false);
        const resp = await getPromptConfigTool(appId, subAgentId);
        if (resp) {
            setExisting(resp);
            setFormData({ name: resp.name, description: resp.description });
        }
        updateDataLoadingState(false, true, false);
    }

    useEffect(() => {
        if (subAgentId) {
            fetchSubAgent()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subAgentId]);

    const buttonText = subAgentId ? 'Update Sub-Agent' : 'Create Sub-Agent';

    return (
        <div ref={rootRef}>
            <Modal show={true} size="md" popup onClose={onCancel} root={rootRef.current ?? undefined}>
                <Modal.Header></Modal.Header>
                <Modal.Body>

                    <form onSubmit={handleOnSubmit}>
                        <div className="space-y-6">
                            <h3 className="text-xl font-medium text-gray-900">
                                {subAgentId ? 'Update Sub-Agent' : 'Create New Sub-Agent'}
                            </h3>

                            {(formState.processing || dataLoadingState.processing) && <FormLoader relative={true} />}

                            {formState.success && <Message text={`Successfully saved sub-agent`} type="success" />}

                            {formState.failed && <Message text={formState.message} type="failure" />}

                            <TextBox
                                type="text"
                                name="name"
                                id="name"
                                label="Name*"
                                value={formData.name}
                                onChange={handleChange}
                                error={error.name}
                                errorMessage={error.nameErrorMessage}
                            />

                            <TextArea
                                type="text"
                                name="description"
                                id="description"
                                label="Description"
                                value={formData.description}
                                placeholder={`Describe what this sub-agent does and when the main agent should delegate to it.\nExample: researches products and compares prices.`}
                                onChange={handleChange}
                                rows={3}
                                error={error.description}
                            />

                            <div className="w-full flex flex-row gap-2 justify-end">
                                <Button color="default" text="Cancel" onClick={onCancel} />
                                {formState.processing ?
                                    <Button type="button" text={buttonText} loading={true} />
                                    :
                                    <Button type="submit" text={buttonText} />
                                }
                            </div>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
        </div>
    )
}

export default SubAgentCreateModel
